import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import OrganizationSearchContext from '../../context/organization_search.context';
import { useQueryParam, StringParam, NumberParam, ArrayParam } from 'use-query-params';
import { ArrayOfHashesParam } from '../../../utilities/use-query-params/array_of_hashes_param';
import PostJobCtaBanner from '../post_job_cta_banner/post_job_cta_banner';
import RaUpgradeBanner from '../ra_upgrade_banner/ra_upgrade_banner';
import SidebarFilters from '../sidebar_filters/sidebar_filters';
import Results from '../results/results';
import { getOrganizations } from '../../services/organization_search.service';
import { parseQueryParamsForApi } from '../../utilities/organization_search.utilities';
import { AlertHandler, ErrorAlert } from '../../../components/react/alert_handler/alert_handler';
import {
  ORG_SIZE_DEFAULT_MAX,
  ORG_SIZE_DEFAULT_MIN,
  SLIDER_STEPS,
} from '../../constants/organization_search.constants';

const alertHandler = new AlertHandler();

const Container = ({ displayNewLogo }) => {
  const { isRecruiter } = useContext(OrganizationSearchContext);
  const [isLoading, setIsLoading] = useState(false);
  const [query, setQuery] = useQueryParam('query[]', StringParam);
  const [userQuery, setUserQuery] = useState(query || '');
  const [types, setTypes] = useQueryParam('filter[types][]', ArrayParam);

  const [orgSizeMin = ORG_SIZE_DEFAULT_MIN.text, setOrgSizeMin] = useQueryParam(
    'filter[organization_size][min]',
    StringParam
  );

  const [orgSizeMax = ORG_SIZE_DEFAULT_MAX.text, setOrgSizeMax] = useQueryParam(
    'filter[organization_size][max]',
    StringParam
  );

  const [sliderArrayInput, setSliderArrayInput] = useState([
    Object.keys(SLIDER_STEPS).find((key) => SLIDER_STEPS[key] === orgSizeMin),
    Object.keys(SLIDER_STEPS).find((key) => SLIDER_STEPS[key] === orgSizeMax),
  ]);

  const [hiringNow, setHiringNow] = useQueryParam('filter[hiring_now]', StringParam);
  const [hiringNowInput, setHiringNowInput] = useState(hiringNow === 'true');
  const [topEmployers, setTopEmployers] = useQueryParam('filter[top_employers_only]', StringParam);
  const [topEmployersInput, setTopEmployersInput] = useState(topEmployers === 'true');
  const [pageNumber = 1, setPageNumber] = useQueryParam('page[number]', NumberParam);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  const [hiringLocations, setHiringLocations] = useQueryParam(
    'filter[hiring_locations][]',
    ArrayParam
  );

  const [officeLocations, setOfficeLocations] = useQueryParam(
    'office_locations[]',
    ArrayOfHashesParam
  );

  /**
   * Whenever users toggles Hiring Now checkbox, change hiringNow query param
   * in order to reflect either `true` in URL or undefined (to remove `filter[hiring_now]`
   * queryParam from URL)
   */
  useEffect(() => {
    setHiringNow(hiringNowInput ? 'true' : undefined);
  }, [hiringNowInput]);

  /**
   * Whenever users toggles Top Employers checkbox, change topEmployers query param
   * in order to reflect either `true` in URL or undefined (to remove `filter[top_employers_only]`
   * queryParam from URL)
   */
  useEffect(() => {
    setTopEmployers(topEmployersInput ? 'true' : undefined);
  }, [topEmployersInput]);

  /**
   * Whenever there is a change in one of the filters, run BE search
   * to get filtered organizations
   */
  useEffect(() => {
    if (isLoading) return;
    setIsLoading(true);
    // To reflect query in URL, if users did not actively submit the query text input yet,
    // make sure the userQuery change is taken into consideration when searching for organizations.
    // In order to do so, use the userQuery as the param for the BE search
    // and update the URL to reflect actual query.
    setQuery(userQuery.trim().length ? userQuery : undefined);
    const queryParams = parseQueryParamsForApi({
      query: userQuery.trim(),
      types,
      pageNumber,
      hiringNow,
      topEmployers,
      hiringLocations,
      orgSizeMin,
      orgSizeMax,
      officeLocations,
    });

    getOrganizations(queryParams)
      .then((response) => {
        setResults(response.data);
        setTotal(response.total);
        setMaxPages(response.page.pages);
      })
      .catch(() => alertHandler.render(ErrorAlert, 'Something went wrong. Please try again.'))
      .finally(() => setIsLoading(false));
  }, [
    query,
    types,
    pageNumber,
    hiringNow,
    topEmployers,
    hiringLocations,
    orgSizeMin,
    orgSizeMax,
    officeLocations,
  ]);

  /**
   * On keyword submission, make sure we remove `query[]` queryParam from URL
   * when there is no keyword in the search box.
   *
   * @param {event} event - Event triggered by browser
   * @returns {undefined}
   */
  const onKeywordSearch = (event) => {
    event.preventDefault();
    setPageNumber(1);
    setQuery(userQuery.trim().length ? userQuery : undefined);
  };

  /**
   * @returns {undefined}
   */
  const clearAllFilters = () => {
    setQuery('');
    setUserQuery('');
    setTopEmployers(undefined);
    setTopEmployersInput(false);
    setTypes([]);
    setSliderArrayInput([ORG_SIZE_DEFAULT_MIN.value, ORG_SIZE_DEFAULT_MAX.value]);
    setOrgSizeMin(ORG_SIZE_DEFAULT_MIN.text);
    setOrgSizeMax(ORG_SIZE_DEFAULT_MAX.text);
    setHiringLocations([]);
    setOfficeLocations([]);
    setHiringNow(undefined);
    setHiringNowInput(false);
    setPageNumber(1);
  };

  /**
   * @type {Node} Render message with search tips
   */
  const renderTipsMessage = (
    <div className='mt-25 org-search-tips d-none d-sm-block'>
      <span className='text-gray-li'>
        Increase your search power by adding <em>AND, OR, (), NOT, </em>
        or &quot;&quot; to your keywords e.g. gender AND (somalia OR kenya) NOT health
      </span>
    </div>
  );

  /**
   * @type {Node} Renders text/query input search box
   */
  const renderQuerySearchBox = (
    <>
      <h1 className='fs-05 text-white search-header-title mb-25'>
        {topEmployers === 'true' ? 'Top Employers' : 'Employer Search'}
      </h1>
      <div className='white-container pt-35 pb-25 ps-35 pe-35'>
        <div className='row'>
          <div className='col-xs-12'>
            <div className='form-group main-filters'>
              <div className='input-group'>
                <input
                  className='form-control mb-25 mb-md-0'
                  aria-label='keyword search'
                  type='text'
                  value={userQuery}
                  onChange={({ target }) => setUserQuery(target.value)}
                  placeholder='Search organizations by name, sector, region or donor experience…'
                />
                <button
                  className='btn btn-secondary mb-2 mb-md-0'
                  type='submit'
                  id='keyword-search'
                >
                  <i className='fal icon-search' />
                  <span className='ms-15'>Search</span>
                </button>
              </div>
            </div>
          </div>
        </div>
        {renderTipsMessage}
      </div>
    </>
  );

  return (
    <fieldset className='m-0 p-0' aria-label='organization search' disabled={isLoading}>
      <form aria-label='form' onSubmit={onKeywordSearch}>
        <div className='search-header py-45 py-md-5'>
          <div className='container'>{renderQuerySearchBox}</div>
        </div>
        {!isRecruiter && topEmployers === 'true' ? (
          <RaUpgradeBanner />
        ) : (
          <PostJobCtaBanner />
        )}
        <div className='container mt-35 mb-48'>
          <div className='row'>
            <SidebarFilters
              isLoading={isLoading}
              types={types || []}
              setTypes={setTypes}
              setPageNumber={setPageNumber}
              hiringNowInput={hiringNowInput}
              setHiringNowInput={setHiringNowInput}
              topEmployersInput={topEmployersInput}
              setTopEmployersInput={setTopEmployersInput}
              hiringLocations={hiringLocations || []}
              setHiringLocations={setHiringLocations}
              officeLocations={officeLocations || []}
              setOfficeLocations={setOfficeLocations}
              orgSizeMin={orgSizeMin}
              setOrgSizeMin={setOrgSizeMin}
              orgSizeMax={orgSizeMax}
              setOrgSizeMax={setOrgSizeMax}
              sliderArrayInput={sliderArrayInput}
              setSliderArrayInput={setSliderArrayInput}
              clearAllFilters={clearAllFilters}
            />
            <Results
              data={results}
              maxPages={maxPages}
              total={total}
              setPageNumber={setPageNumber}
              isLoading={isLoading}
              pageNumber={pageNumber}
              displayNewLogo={displayNewLogo}
            />
          </div>
        </div>
      </form>
    </fieldset>
  );
};

Container.propTypes = {
  displayNewLogo: PropTypes.bool.isRequired,
};

/* eslint-enable no-unused-vars */
export default Container;
